import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Trash2, Check } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { useInquiry } from "@/lib/inquiry-store";
import { fallbackImage } from "@/lib/catalog";
import { sendInquiry } from "@/lib/inquiry.functions";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/inquiry")({
  head: () => ({
    meta: [
      ...seo({
        title: "Your Inquiry — QT Foods",
        description: "Review and send your wholesale inquiry to QT Foods.",
      }),
    ],
  }),
  component: Page,
});

function Page() {
  const { items, setQuantity, setNote, remove, clear } = useInquiry();
  const send = useServerFn(sendInquiry);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    company: "",
    contactName: "",
    email: "",
    phone: "",
    country: "",
    message: "",
    website: "",
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (items.length === 0) {
      setError("Add at least one product to your inquiry.");
      return;
    }
    setSubmitting(true);
    try {
      await send({
        data: {
          ...form,
          items: items.map((it) => ({
            productHandle: it.productHandle,
            productName: it.productName,
            categoryName: it.categoryName,
            quantity: it.quantity,
            note: it.note,
          })),
        },
      });
      clear();
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: unknown) {
      console.error(err);
      setError("Something went wrong sending your inquiry. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <SiteLayout>
        <section className="mx-auto max-w-2xl px-5 py-24 text-center lg:px-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="h-7 w-7" />
          </div>
          <h1 className="mt-6 font-serif text-4xl">Inquiry sent</h1>
          <p className="mt-4 text-muted-foreground">
            Thank you. Our team will review your request and respond within 1–2 business days.
          </p>
          <button
            onClick={() => navigate({ to: "/collections" })}
            className="mt-8 inline-block rounded-sm bg-primary px-6 py-3 text-sm font-medium tracking-wide text-primary-foreground hover:bg-primary/90"
          >
            Continue browsing
          </button>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
          Wholesale request
        </p>
        <h1 className="mt-3 font-serif text-5xl md:text-6xl">Your inquiry</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Review the products below, adjust quantities, then send your request. Our team will reply
          with pricing, MOQ and lead times.
        </p>

        {items.length === 0 ? (
          <div className="mt-16 border border-dashed border-border p-12 text-center">
            <p className="text-muted-foreground">Your inquiry list is empty.</p>
            <Link
              to="/collections"
              className="mt-6 inline-block rounded-sm bg-primary px-6 py-3 text-sm font-medium tracking-wide text-primary-foreground hover:bg-primary/90"
            >
              Browse catalog
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <ul className="divide-y divide-border border-y border-border">
                {items.map((it) => (
                  <li key={it.productHandle} className="flex gap-4 py-5">
                    <img
                      src={it.imageUrl || fallbackImage()}
                      alt=""
                      className="h-24 w-24 flex-shrink-0 rounded-sm object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          {it.categoryName && (
                            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                              {it.categoryName}
                            </p>
                          )}
                          <p className="font-serif text-lg leading-snug">{it.productName}</p>
                        </div>
                        <button
                          onClick={() => remove(it.productHandle)}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">
                          Qty
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={it.quantity}
                          onChange={(e) =>
                            setQuantity(it.productHandle, parseInt(e.target.value) || 1)
                          }
                          className="w-20 border border-border bg-card px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
                        />
                      </div>
                      <textarea
                        rows={2}
                        placeholder="Add a note (packaging, branding, variants…)"
                        value={it.note ?? ""}
                        onChange={(e) => setNote(it.productHandle, e.target.value)}
                        className="mt-3 w-full border border-border bg-card px-3 py-2 text-sm focus:border-primary focus:outline-none"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <form
              onSubmit={onSubmit}
              className="border border-border bg-card p-6 lg:sticky lg:top-24 lg:self-start"
            >
              <h2 className="font-serif text-2xl">Your details</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll reply by email within 1–2 business days.
              </p>
              <div className="mt-5 grid gap-4">
                <Field
                  label="Company"
                  required
                  value={form.company}
                  onChange={(v) => setForm({ ...form, company: v })}
                />
                <Field
                  label="Contact name"
                  required
                  value={form.contactName}
                  onChange={(v) => setForm({ ...form, contactName: v })}
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                  />
                  <Field
                    label="Country"
                    value={form.country}
                    onChange={(v) => setForm({ ...form, country: v })}
                  />
                </div>
                {/* Honeypot field */}
                <div className="sr-only">
                  <label htmlFor="website">Don't fill this out if you're human</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Message
                  </span>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1.5 w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  />
                </label>
              </div>
              {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full rounded-sm bg-primary px-6 py-3 text-sm font-medium uppercase tracking-[0.15em] text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Inquiry"}
              </button>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                {items.length} item{items.length === 1 ? "" : "s"} will be sent.
              </p>
            </form>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
        {required && " *"}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
      />
    </label>
  );
}
